import React, { useState, useEffect } from "react";
import { X, CreditCard, IndianRupee, Wallet } from "lucide-react";
import toast from "react-hot-toast";
import { AppointmentService } from "../../../../services/api/appointment.service";
import { DoctorPayment } from "../types";

interface UpdatePendingPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  appointmentId: string;
  doctorPayments?: DoctorPayment;
  reload: () => void;
}

export const UpdatePendingPaymentModal: React.FC<UpdatePendingPaymentModalProps> = ({
  isOpen,
  onClose,
  appointmentId,
  doctorPayments,
  reload,
}) => {
  const [pendingPaymentType, setPendingPaymentType] = useState<"cash" | "online" | "split">("cash");
  const [pendingCashAmount, setPendingCashAmount] = useState<number>(0);
  const [pendingOnlineAmount, setPendingOnlineAmount] = useState<number>(0);
  const [paymentTxnId, setPaymentTxnId] = useState<string>("");
  const [razorpayOrderId, setRazorpayOrderId] = useState<string>("");
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const due = doctorPayments?.balance_due || 0;
      setPendingPaymentType("cash");
      setPendingCashAmount(due);
      setPendingOnlineAmount(0);
      setPaymentTxnId("");
      setRazorpayOrderId("");
    }
  }, [isOpen, doctorPayments]);

  if (!isOpen) return null;

  const handlePendingTypeChange = (type: "cash" | "online" | "split") => {
    setPendingPaymentType(type);
    const due = doctorPayments?.balance_due || 0;
    if (type === "cash") {
      setPendingCashAmount(due);
      setPendingOnlineAmount(0);
    } else if (type === "online") {
      setPendingCashAmount(0);
      setPendingOnlineAmount(due);
    } else if (type === "split") {
      const half = Math.round(due / 2);
      setPendingCashAmount(half);
      setPendingOnlineAmount(due - half);
    }
  };

  const handleSubmit = async () => {
    const due = doctorPayments?.balance_due || 0;
    const cash = Number(pendingCashAmount) || 0;
    const online = Number(pendingOnlineAmount) || 0;

    if (cash < 0 || online < 0) {
      toast.error("Amounts cannot be negative");
      return;
    }

    if (cash + online === 0) {
      toast.error("Please enter an amount to collect");
      return;
    }

    if (pendingPaymentType === "split" && cash + online !== due) {
      toast.error(`Split payments (₹${cash + online}) must total balance due (₹${due})`);
      return;
    }

    let splitPayments: Array<{
      paymentMethod: string;
      amount: number;
      paymentTxnId?: string;
      razorpayOrderId?: string;
    }> = [];

    if (pendingPaymentType === "cash") {
      splitPayments = [{ paymentMethod: "cash", amount: cash }];
    } else if (pendingPaymentType === "online") {
      splitPayments = [
        {
          paymentMethod: "online",
          amount: online,
          ...(paymentTxnId ? { paymentTxnId } : {}),
          ...(razorpayOrderId ? { razorpayOrderId } : {}),
        },
      ];
    } else {
      splitPayments = [
        { paymentMethod: "cash", amount: cash },
        {
          paymentMethod: "online",
          amount: online,
          ...(paymentTxnId ? { paymentTxnId } : {}),
          ...(razorpayOrderId ? { razorpayOrderId } : {}),
        },
      ].filter((p) => p.amount > 0);
    }

    const payload = {
      appointmentId,
      splitPayments,
    };

    setIsUpdating(true);
    try {
      const response = await AppointmentService.updatePendingPayment(payload);
      if (response.success) {
        toast.success("Pending payment updated successfully");
        onClose();
        reload();
      } else {
        toast.error(response.message || "Failed to update pending payment");
      }
    } catch (error) {
      toast.error("Failed to update pending payment");
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden border border-gray-100">
        {/* Minimal Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Update Pending Payment</h3>
            <p className="text-xs text-gray-500">Collect remaining balance for this appointment</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-1.5 rounded-lg transition-colors"
            disabled={isUpdating}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-5 max-h-[75vh] overflow-y-auto">
          {/* Info Card */}
          <div className="bg-gray-50/70 p-3.5 rounded-xl border border-gray-100 text-xs space-y-1.5">
            <div className="flex justify-between">
              <span className="text-gray-500">Total Consultation Fee:</span>
              <span className="font-semibold text-gray-900">₹{doctorPayments?.amount || 0}</span>
            </div>
            <div className="flex justify-between border-t border-gray-200/80 pt-1.5 mt-1">
              <span className="font-medium text-gray-700">Remaining Balance Due:</span>
              <span className="font-bold text-amber-700 text-sm">₹{doctorPayments?.balance_due || 0}</span>
            </div>
          </div>

          {/* Payment Type Selection */}
          <div className="space-y-2.5">
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
              Payment Method
            </label>
            <div className="grid grid-cols-3 gap-2.5">
              <button
                type="button"
                onClick={() => handlePendingTypeChange("cash")}
                className={`flex flex-col items-center justify-center p-3 rounded-xl border text-xs font-medium gap-1 transition-all ${
                  pendingPaymentType === "cash"
                    ? "bg-emerald-50/60 border-emerald-600 text-emerald-800"
                    : "border-gray-200 hover:bg-gray-50 text-gray-700"
                }`}
              >
                <IndianRupee className="w-4 h-4 text-gray-600" />
                Cash
              </button>
              <button
                type="button"
                onClick={() => handlePendingTypeChange("online")}
                className={`flex flex-col items-center justify-center p-3 rounded-xl border text-xs font-medium gap-1 transition-all ${
                  pendingPaymentType === "online"
                    ? "bg-emerald-50/60 border-emerald-600 text-emerald-800"
                    : "border-gray-200 hover:bg-gray-50 text-gray-700"
                }`}
              >
                <CreditCard className="w-4 h-4 text-gray-600" />
                Online
              </button>
              <button
                type="button"
                onClick={() => handlePendingTypeChange("split")}
                className={`flex flex-col items-center justify-center p-3 rounded-xl border text-xs font-medium gap-1 transition-all ${
                  pendingPaymentType === "split"
                    ? "bg-emerald-50/60 border-emerald-600 text-emerald-800"
                    : "border-gray-200 hover:bg-gray-50 text-gray-700"
                }`}
              >
                <Wallet className="w-4 h-4 text-gray-600" />
                Split
              </button>
            </div>
          </div>

          {/* Dynamic Inputs */}
          {pendingPaymentType === "cash" && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Cash Amount to Collect (₹)
              </label>
              <input
                type="number"
                min={1}
                max={doctorPayments?.balance_due || undefined}
                value={pendingCashAmount}
                onChange={(e) => setPendingCashAmount(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-emerald-500 bg-white"
              />
            </div>
          )}

          {pendingPaymentType === "online" && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Online Amount to Collect (₹)
                </label>
                <input
                  type="number"
                  min={1}
                  max={doctorPayments?.balance_due || undefined}
                  value={pendingOnlineAmount}
                  onChange={(e) => setPendingOnlineAmount(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-emerald-500 bg-white"
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-600 mb-1">
                  Payment Txn ID / Razorpay Order ID (Optional)
                </label>
                <input
                  type="text"
                  placeholder="pay_XYZ987654"
                  value={paymentTxnId}
                  onChange={(e) => setPaymentTxnId(e.target.value)}
                  className="w-full px-3 py-1.5 border rounded-lg text-xs bg-white focus:ring-1 focus:ring-emerald-500"
                />
              </div>
            </div>
          )}

          {pendingPaymentType === "split" && (
            <div className="p-3.5 bg-gray-50/80 rounded-xl border border-gray-200 space-y-3">
              <h4 className="text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                Split Breakdown (Balance Due: ₹{doctorPayments?.balance_due || 0})
              </h4>
              <div className="grid grid-cols-2 gap-2.5">
                <div>
                  <label className="block text-[11px] font-medium text-gray-600 mb-1">
                    Cash Paid (₹)
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={pendingCashAmount}
                    onChange={(e) => setPendingCashAmount(Number(e.target.value))}
                    className="w-full px-3 py-1.5 border rounded-lg text-xs bg-white focus:ring-1 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-600 mb-1">
                    Online Paid (₹)
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={pendingOnlineAmount}
                    onChange={(e) => setPendingOnlineAmount(Number(e.target.value))}
                    className="w-full px-3 py-1.5 border rounded-lg text-xs bg-white focus:ring-1 focus:ring-emerald-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-medium text-gray-600 mb-1">
                  Payment Txn ID / Razorpay Order ID (Optional)
                </label>
                <input
                  type="text"
                  placeholder="pay_XYZ987654"
                  value={paymentTxnId}
                  onChange={(e) => setPaymentTxnId(e.target.value)}
                  className="w-full px-3 py-1.5 border rounded-lg text-xs bg-white focus:ring-1 focus:ring-emerald-500"
                />
              </div>

              {pendingCashAmount + pendingOnlineAmount !== (doctorPayments?.balance_due || 0) && (
                <p className="text-[11px] text-red-600 font-medium">
                  ⚠️ Cash (₹{pendingCashAmount}) + Online (₹{pendingOnlineAmount}) = ₹{pendingCashAmount + pendingOnlineAmount}. Expected: ₹{doctorPayments?.balance_due || 0}.
                </p>
              )}
            </div>
          )}

          {/* Summary Box */}
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 text-xs space-y-2">
            <div className="flex justify-between text-gray-600">
              <span>Collecting Now:</span>
              <span className="font-bold text-emerald-700 text-sm">
                ₹{(Number(pendingCashAmount) || 0) + (Number(pendingOnlineAmount) || 0)}
              </span>
            </div>
            <div className="flex justify-between text-gray-600">
              <span>New Balance Due:</span>
              <span className="font-semibold text-gray-900">
                ₹{Math.max(0, (doctorPayments?.balance_due || 0) - ((Number(pendingCashAmount) || 0) + (Number(pendingOnlineAmount) || 0)))}
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50/50">
          <button
            type="button"
            onClick={onClose}
            disabled={isUpdating}
            className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 text-xs font-medium hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isUpdating}
            className="px-5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold transition-colors disabled:opacity-50 shadow-sm"
          >
            {isUpdating ? "Updating..." : "Collect & Update Payment"}
          </button>
        </div>
      </div>
    </div>
  );
};
